import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import jsPDF from "jspdf";
import {
  Layout,
  Card,
  Typography,
  Input,
  Button,
  message,
  Flex,
  Breadcrumb as AntBreadcrumb,
  Form,
  Radio,
  InputNumber,
} from "antd";

import ReviewForm, {
  Option,
  AssessmentQuestion,
  getPrefix,
} from "../../components/AssessmentReview/ReviewForm";
import ErrorMessageCard from "../../components/Error/ErrorMessageCard";
import Loader from "../../components/Layout/Loader";
import ReviewHeader from "../../components/AssessmentReview/ReviewHeader";

import {
  getTrainignAssessmentById,
  submitAssessmentReview,
} from "../../services/ApiActions/adminAct";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text } = Typography;

const TrainingAssessmentReviewPage = () => {
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState("");
  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentDetails, setAssessmentDetails] = useState<any>(null);
  const [passFailStatus, setPassFailStatus] = useState<string | null>(null);
  const [isDeclared, setIsDeclared] = useState(false);

  const componentRef = useRef(null);
  const navigate = useNavigate();

  const user_id = searchParams.get("user_id");
  const assessment_id = searchParams.get("id");
  const return_url = searchParams.get("return_url");

  useEffect(() => {
    fetchAssessmentDetails();

    // Return a cleanup function to reset form when component unmounts
    return () => {
      form.resetFields();
    };
  }, [assessment_id, user_id]);

  useEffect(() => {
    if (error) {
      message.error(error);
      setError(null);
    }
  }, [error]);

  const fetchAssessmentDetails = async () => {
    if (!user_id || !assessment_id) return;

    setLoading(true);
    setError(null);

    try {
      const response: any = await getTrainignAssessmentById(
        assessment_id,
        user_id
      );

      const assessmentData = response.data;
      const failPass =
        assessmentData.result && assessmentData.result.toLowerCase();

      // Debug the response structure
      console.log("Assessment data structure:", assessmentData);

      setAssessmentDetails(assessmentData);
      setAssessmentName(assessmentData.name || "");
      setPassFailStatus(failPass);
      setReviewerName(assessmentData.certification_reviewed_by || "");
      setIsDeclared(failPass === "fail" || failPass === "pass");

      // Need to wait for state to update before setting form values
      /*setTimeout(() => {
        if (assessmentData.questions) {
          const initialValues = getInitialFormValues(assessmentData.questions);
          console.log("Setting initial form values:", initialValues);
          form.setFieldsValue(initialValues);
        }
      }, 0);*/
    } catch (error) {
      console.error("Error fetching assessment:", error);
      setError("Failed to fetch assessment details");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (values: Record<string, any>) => {
    console.log(values);
    if (!assessmentDetails) return;

    // Extract form values
    const getMarks = (fieldKey: string) => {
      const marksValue = Object.entries(values).find(
        ([key]) => key == `marks-${fieldKey}`
      );
      return marksValue?.[1];
    };
    const formResponses = Object.entries(values)
      .filter(([key]) => key.startsWith("question-msq"))
      .map(([key, value]) => {
        const obtainedMarks = getMarks(key.replace("question-msq-", ""));
        return {
          certification_question_id: Number(key.replace("question-msq-", "")),
          ...(obtainedMarks !== undefined &&
            obtainedMarks !== null && { obtained_marks: obtainedMarks }),
        };
      });

    // Extract marks values

    // Construct payload for submission
    const payload = {
      responses: formResponses,
      id: Number(assessmentDetails.id),
      user_id: Number(user_id),
    };

    console.log("Submitting review with payload:", payload);
    // Here you would call your API to submit the review
    const response: any = await submitAssessmentReview(payload);

    // Show a success message

    message.success("Review submitted successfully");
    navigate(`/admin-training-assessment-users/${assessmentDetails.id}`);
  };

  const downloadPdf = () => {
    if (!assessmentDetails) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const maxLineWidth = pageWidth - margin * 2;
    const radioButtonSize = 1; // Radius of the radio button
    let yOffset = margin;

    // Add title
    pdf.setFontSize(14);
    pdf.text(assessmentDetails.name || "Assessment", margin, yOffset);
    yOffset += 10;

    // Add result if available
    if (assessmentDetails.result) {
      pdf.setFontSize(12);
      const resultLines = pdf.splitTextToSize(
        `Result: ${assessmentDetails.result}`,
        maxLineWidth
      );
      pdf.text(resultLines, margin, yOffset);
      yOffset += resultLines.length * 7;
    }

    // Add questions and answers

    assessmentDetails.questions.forEach(
      (item: AssessmentQuestion, i: number) => {
        pdf.setFontSize(11);
        const questionLines = pdf.splitTextToSize(
          `${i + 1}. ${item.question.trim()}`,
          maxLineWidth
        );

        // Handle page overflow
        if (yOffset + questionLines.length * 7 > pageHeight - margin) {
          pdf.addPage();
          yOffset = margin;
        }

        pdf.text(questionLines, margin, yOffset);
        yOffset += questionLines.length * 7;

        // Check if there are options (for multiple choice questions)
        let ansLabel = "";
        let answer = item.submitted_answer;
        const answerMargin = margin + 5;

        if (item.is_multiple_choice_question && item.options.length > 0) {
          pdf.setFontSize(10);
          ansLabel = "Correct Answer: ";

          item.options.forEach((option: any, index: number) => {
            // Handle page overflow within options
            if (yOffset > pageHeight - margin) {
              pdf.addPage();
              yOffset = margin;
            }

            // Draw the radio button circle
            const radioButtonX = margin + 5;
            const radioButtonY = yOffset - 1; // Adjust for alignment
            const isSelected = item.submitted_answer == String(index + 1);

            if (isSelected) {
              answer = option.option.trim();
              pdf.setFillColor(0, 0, 0); // Black for selected
            } else {
              pdf.setFillColor(255, 255, 255); // White for unselected
            }

            pdf.circle(radioButtonX, radioButtonY, radioButtonSize, "F"); // Draw filled circle
            pdf.setFillColor(0, 0, 0); // Set color for the border
            pdf.circle(radioButtonX, radioButtonY, radioButtonSize, "D"); // Draw border

            const optionText = `${option.option.trim()}`;
            const optionLines = pdf.splitTextToSize(
              optionText,
              maxLineWidth - radioButtonSize - 10
            );

            optionLines.forEach((line: any) => {
              pdf.text(line, radioButtonX + radioButtonSize + 2, yOffset);
              yOffset += 7;
            });
          });
        }

        // Show correct/wrong indicator for multiple choice
        if (item.is_multiple_choice_question) {
          if (yOffset > pageHeight - margin) {
            pdf.addPage();
            yOffset = margin;
          }

          const statusText = item.is_correct ? "Correct" : "Wrong";
          pdf.text(statusText, answerMargin, yOffset);
          yOffset += 7;
        }

        // Show correct answer if wrong or not multiple choice
        if (
          (item.is_multiple_choice_question && !item.is_correct) ||
          !item.is_multiple_choice_question
        ) {
          if (yOffset > pageHeight - margin) {
            pdf.addPage();
            yOffset = margin;
          }

          const answerLines = pdf.splitTextToSize(
            `${ansLabel} ${answer.trim()}`,
            maxLineWidth
          );

          answerLines.forEach((line: any) => {
            pdf.text(line, answerMargin, yOffset);
            yOffset += 7;
          });
        }

        // Add a little space between questions
        yOffset += 5;
      }
    );

    pdf.save(`${assessmentDetails.name || "Assessment"}.pdf`);
  };

  const navigateBack = () => {
    navigate(
      return_url ? return_url : `/admin-training-assessment-users/${assessment_id}`
    );
  };

  if (loading) {
    return <Loader style={{ marginTop: "20%", justifyContent: "center" }} />;
  }
  const isReviewed =
    assessmentDetails?.certification_status?.toLowerCase() === "reviewed";
  const {
    is_attempted,
    obtained_marks,
    total_marks,
    passing_percentage,
    questions,
  } = assessmentDetails ?? {};
  console.log("assessmentDetails ---", is_attempted);
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Flex align="center">
        <div style={{ flexGrow: 1 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ArrowLeftOutlined
              style={{ fontSize: "23px", marginRight: "10px" }}
              onClick={navigateBack}
            />
            <Typography.Title level={3} style={{ margin: 0 }}>
              {assessmentName ? assessmentName : "Back"}
            </Typography.Title>
          </div>
          <AntBreadcrumb style={{ margin: "0 0 16px" }}>
            <AntBreadcrumb.Item>LMS</AntBreadcrumb.Item>
            {assessmentName && (
              <AntBreadcrumb.Item>{assessmentName}</AntBreadcrumb.Item>
            )}
          </AntBreadcrumb>
        </div>
        {isDeclared && (
          <div>
            <div className="tab-button" onClick={downloadPdf}>
              <DownloadOutlined /> Download Assessment
            </div>
          </div>
        )}
      </Flex>

      <Layout>
        <Content
          className="site-layout-background"
          style={{ padding: "0 0 0 10px", margin: 0 }}
        >
          <Card style={{ minHeight: "100%" }} ref={componentRef}>
            <ReviewHeader
              reviewerName={reviewerName}
              passFailStatus={passFailStatus}
              is_attempted={is_attempted}
              obtained_marks={obtained_marks}
              total_marks={total_marks}
              passing_percentage={passing_percentage}
            />

            {!is_attempted ? (
              <ErrorMessageCard message="To review this assessment, the user must first attempt it." />
            ) : (
              <ReviewForm
                is_attempted={is_attempted}
                isDeclared={isDeclared}
                isReviewed={isReviewed}
                questions={questions}
                handleFormSubmit={(values) => handleFormSubmit(values)}
              />
            )}
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default TrainingAssessmentReviewPage;
